import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconShoppingCart } from "@tabler/icons-react";
import type { ProductVariantDto } from "../../../../api";
import { Image } from "../../../../components/common/image";
import { Button } from "../../../../components/common/button";
import { Text } from "../../../../components/common/typography";
import { GatedPrice } from "../../../../components/common/gated-price";
import type { BreadcrumbItem } from "../../../../components/common/breadcrumbs";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useCartStore } from "../../../../hooks/stores/useCartStore";
import { useCartUiStore } from "../../../../hooks/stores/useCartUiStore";
import { useCreateQuotation } from "../../../cart/hooks/useCreateQuotation";
import { getVariantUnitPrice } from "../../utils/card-props";

export interface ComparisonRow {
  label: string;
  accessor: (p: ProductVariantDto) => string | number | null | undefined;
}

interface ComparisonTableProps {
  products: ProductVariantDto[];
  rows: ComparisonRow[];
}

type PricelistItem = { id: number; minQuantity: number; fixedPrice: number };

function ProductHeader({ product }: { product: ProductVariantDto }) {
  return (
    <div className="flex flex-1 min-w-0 flex-col items-center justify-center gap-3 p-3">
      <div className="h-40 w-60 overflow-hidden rounded-2xl print:h-24 print:w-32">
        <Image
          image={product.image}
          alt={product.name ?? "Product"}
          size="w-full h-full"
          showPlaceholder
        />
      </div>
      <Text variant="bodyBold" tone="dark" className="text-center">
        {product.name ?? "—"}
      </Text>
    </div>
  );
}

function SpecRow({
  label,
  products,
  accessor,
}: {
  label: string;
  products: ProductVariantDto[];
  accessor: (p: ProductVariantDto) => string | number | null | undefined;
}) {
  return (
    <>
      <div className="flex">
        <div className="w-[220px] shrink-0 border-r border-borderDefault bg-bgLight flex items-center py-4 pl-5 pr-4">
          <Text variant="bodyXs" tone="muted">{label}</Text>
        </div>
        {products.map((product, i) => (
          <div
            key={product.id}
            className={`flex flex-1 min-w-0 items-center justify-center py-4 px-3 ${i > 0 ? "border-l border-dashed border-borderDefault" : ""}`}
          >
            <Text variant="bodySm" tone="dark">
              {String(accessor(product) ?? "—")}
            </Text>
          </div>
        ))}
      </div>
      <div className="h-px bg-borderDefault" />
    </>
  );
}

function PriceRow({ products }: { products: ProductVariantDto[] }) {
  return (
    <div className="flex">
      <div className="w-[220px] shrink-0 border-r border-borderDefault bg-bgLight" />
      {products.map((product, i) => {
        const unitPrice = getVariantUnitPrice(product);
        const standardPrice = product.standardPrice;
        const hasDiscount =
          unitPrice != null && standardPrice != null && standardPrice > unitPrice;
        // A tier without a positive price is unset, not free.
        const tiers = (
          (product as unknown as { pricelistItems?: PricelistItem[] }).pricelistItems ?? []
        ).filter((t) => t.minQuantity > 1 && t.fixedPrice > 0);

        return (
          <div
            key={product.id}
            className={`flex flex-1 min-w-0 flex-col gap-0.5 px-5 py-3 ${i > 0 ? "border-l border-dashed border-borderDefault" : ""}`}
          >
            {hasDiscount ? (
              <Text variant="bodyXs" tone="muted" inline className="line-through">
                <GatedPrice>QAR {standardPrice.toLocaleString()}</GatedPrice>
              </Text>
            ) : (
              <div className="h-4" />
            )}
            {unitPrice != null ? (
              <>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold text-textDark">
                    <GatedPrice>QAR {unitPrice.toLocaleString()}</GatedPrice>
                  </span>
                  <Text variant="bodyXs" tone="muted" inline>/unit</Text>
                </div>
                <Text variant="bodyXs" tone="dark">Excl. VAT · 24h validity</Text>
              </>
            ) : (
              <>
                <Text variant="bodyBold" tone="dark">Price unavailable</Text>
                <Text variant="bodyXs" tone="muted">Request a quotation for pricing</Text>
              </>
            )}

            {tiers.length > 0 && (
              <div className="mt-2 flex flex-col gap-1 border-t border-borderDefault pt-2">
                {tiers.map((tier) => (
                  <div key={tier.id} className="flex items-center justify-between">
                    <Text variant="bodyXs" tone="muted">{tier.minQuantity}+ units</Text>
                    <Text variant="bodyXsBold" tone="dark">
                      <GatedPrice>QAR {tier.fixedPrice.toLocaleString()}</GatedPrice>
                    </Text>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ActionsRow({ products }: { products: ProductVariantDto[] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const upstreamTrail =
    (location.state as { breadcrumbTrail?: BreadcrumbItem[] } | null)
      ?.breadcrumbTrail ?? null;
  const addToCart = useCartStore((s) => s.addToCart);
  const playFlyToCart = useCartUiStore((s) => s.playFlyToCart);
  const { createQuotation, isLoading: isRequestingQuotation } = useCreateQuotation();
  // The hook's loading flag is shared by every column, so remember which
  // variant asked for the quotation and show the progress label only there.
  // The other columns stay disabled meanwhile — one quotation at a time.
  const [quotingVariantId, setQuotingVariantId] = useState<number | null>(null);

  return (
    <div className="flex">
      <div className="w-[220px] shrink-0 border-r border-borderDefault bg-bgLight" />
      {products.map((product, i) => {
        const templateId = product.productTemplateId ?? product.id;
        const trail: BreadcrumbItem[] = [
          ...(upstreamTrail ?? [{ label: "Catalog", path: AppRoutes.PRODUCTS }]),
          {
            label: "Compare products",
            path: `${location.pathname}${location.search}`,
          },
        ];
        const isQuotingThis = isRequestingQuotation && quotingVariantId === product.id;

        return (
          <div
            key={product.id}
            className={`flex flex-1 min-w-0 flex-col items-stretch gap-1.5 px-3.5 py-3 ${i > 0 ? "border-l border-dashed border-borderDefault" : ""}`}
          >
            <Button
              variant="primary"
              fullWidth
              size="sm"
              leftIcon={<IconShoppingCart size={16} />}
              onClick={(e) => {
                addToCart(product, 1);
                playFlyToCart({ source: e.currentTarget, image: product.image ?? undefined });
              }}
            >
              Add to cart
            </Button>
            <Button
              variant="secondary"
              fullWidth
              size="sm"
              onClick={() => {
                setQuotingVariantId(product.id);
                void createQuotation([{ productId: product.id, quantity: 1 }]);
              }}
              disabled={isRequestingQuotation}
            >
              {isQuotingThis ? "Creating quotation…" : "Request quotation"}
            </Button>
            <Button
              variant="underlinedGhost"
              fullWidth
              size="sm"
              onClick={() => {
                navigate(AppRoutes.PRODUCT_DETAILS.replace(":id", String(templateId)), {
                  state: { breadcrumbTrail: trail },
                });
              }}
            >
              View full details
            </Button>
          </div>
        );
      })}
    </div>
  );
}

export function ComparisonTable({ products, rows }: ComparisonTableProps) {
  return (
    <div className="overflow-x-auto rounded-2xl border-2 border-borderDefault bg-bgWhite print:overflow-visible">
      {/* Product headers */}
      <div className="flex">
        <div className="w-[220px] shrink-0 border-r border-borderDefault bg-bgLight flex items-center py-5 pl-5 pr-4">
          <Text variant="bodyXs" tone="muted">Products</Text>
        </div>
        {products.map((product, i) => (
          <div
            key={product.id}
            className={i > 0 ? "border-l border-dashed border-borderDefault flex-1 min-w-0" : "flex-1 min-w-0"}
          >
            <ProductHeader product={product} />
          </div>
        ))}
      </div>

      <div className="h-px bg-borderDefault" />

      {/* Spec rows */}
      {rows.map((row) => (
        <SpecRow
          key={row.label}
          label={row.label}
          products={products}
          accessor={row.accessor}
        />
      ))}

      {/* Price row */}
      <PriceRow products={products} />

      {/* Actions row */}
      <ActionsRow products={products} />
    </div>
  );
}
